/**
 * Visitor Session Client — Browser-Side API Wrapper
 *
 * Communicates with the Next.js API routes at /api/session/*
 * to manage cart IDs in the Redis-backed visitor session.
 *
 * All methods are fire-and-forget safe (catch errors internally).
 * The kp_sid cookie is sent automatically by the browser.
 */

import type {CartSessionResponse, LoginTransitionResponse} from './visitor-session.types';

// ─── Internal Helpers ─────────────────────────────────────────────────────────

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
        },
        ...options,
    });

    if (!response.ok) {
        throw new Error(`Session API error: ${response.status} ${response.statusText}`);
    }

    return response.json() as Promise<T>;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export const visitorSessionClient = {
    /**
     * Get cart ID and pending guest cart ID for the given store.
     */
    async getCartId(storeCode: string): Promise<CartSessionResponse> {
        try {
            return await fetchJson<CartSessionResponse>(
                `/api/session/cart?storeCode=${encodeURIComponent(storeCode)}`,
            );
        } catch {
            return {cartId: null, pendingGuestCartId: null, authContextChanged: false};
        }
    },

    /**
     * Save the active cart ID for the given store.
     */
    async setCartId(storeCode: string, cartId: string): Promise<boolean> {
        try {
            await fetchJson<{success: boolean}>('/api/session/cart', {
                method: 'POST',
                body: JSON.stringify({storeCode, cartId}),
            });
            return true;
        } catch {
            return false;
        }
    },

    /**
     * Clear the active cart ID for the given store (e.g., on cart error).
     */
    async clearCartId(storeCode: string): Promise<boolean> {
        try {
            await fetchJson<{success: boolean}>('/api/session/cart', {
                method: 'DELETE',
                body: JSON.stringify({storeCode}),
            });
            return true;
        } catch {
            return false;
        }
    },

    /**
     * Clear a pending guest cart for a store (after successful deferred merge).
     */
    async clearGuestCart(storeCode: string): Promise<boolean> {
        try {
            await fetchJson<{success: boolean}>('/api/session/guest-cart', {
                method: 'DELETE',
                body: JSON.stringify({storeCode}),
            });
            return true;
        } catch {
            return false;
        }
    },

    /**
     * Notify session that user has logged in.
     * Moves guest carts to guestCarts for deferred merge.
     * Returns the guest cart ID for the current store (for immediate merge).
     */
    async notifyLogin(
        currentStoreCode: string,
    ): Promise<LoginTransitionResponse> {
        try {
            return await fetchJson<LoginTransitionResponse>(
                '/api/session/auth-transition',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        type: 'login',
                        currentStoreCode,
                    }),
                },
            );
        } catch {
            return {guestCartIdForCurrentStore: null};
        }
    },

    /**
     * Notify session that user has logged out.
     * Clears all carts and guest carts.
     */
    async notifyLogout(): Promise<boolean> {
        try {
            await fetchJson<{success: boolean}>('/api/session/auth-transition', {
                method: 'POST',
                body: JSON.stringify({type: 'logout'}),
            });
            return true;
        } catch {
            return false;
        }
    },
};

